import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Search, Filter, DollarSign, Calendar, UserCheck, X, Pencil, Save, Plus, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ShieldCheck, ShieldAlert, ShieldOff, CheckCircle, Image } from 'lucide-react';

const PROOF_STATUS_OPTIONS = [
  { value: '', label: 'All Proofs' },
  { value: 'verified', label: 'Verified' },
  { value: 'flagged', label: 'Flagged' },
  { value: 'unverified', label: 'Unverified' },
];

const ProofBadge = ({ status }) => {
  if (!status || status === 'unverified') return <span className="text-gray-300 text-xs">—</span>;
  const styles = {
    verified: { cls: 'bg-green-100 text-green-700', Icon: ShieldCheck, label: 'Verified' },
    flagged:  { cls: 'bg-yellow-100 text-yellow-700', Icon: ShieldAlert, label: 'Flagged' },
    rejected: { cls: 'bg-red-100 text-red-700', Icon: ShieldOff, label: 'Rejected' },
  };
  const s = styles[status] || styles.flagged;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${s.cls}`}>
      <s.Icon size={11} /> {s.label}
    </span>
  );
};

const PAGE_SIZE_OPTIONS = [50, 100, 200];
const PAYMENT_METHODS = [
  { value: '', label: 'All Methods' },
  { value: 'cash', label: 'Cash' },
  { value: 'jazzcash', label: 'JazzCash' },
  { value: 'easypaisa', label: 'EasyPaisa' },
  { value: 'bank', label: 'Bank Transfer' },
];

const AllCollections = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [proofFilter, setProofFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  // Edit state
  const [userRole, setUserRole] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [proofImageUrl, setProofImageUrl] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [editForm, setEditForm] = useState({ amount_collected: '', payment_method: '', transaction_id: '', notes: '' });
  const [saving, setSaving] = useState(false);

  // Add entry state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ amount_collected: '', payment_method: 'cash', transaction_id: '', notes: '', collected_at: '' });
  const [addProofImage, setAddProofImage] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchingCustomers, setSearchingCustomers] = useState(false);

  const canEdit = userRole === 'admin' || userRole === 'manager';

  const fetchCollections = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('page_size', pageSize);
      if (searchTerm)   params.set('search', searchTerm);
      if (methodFilter) params.set('payment_method', methodFilter);
      if (proofFilter)  params.set('payment_proof_status', proofFilter);
      if (dateFrom)     params.set('date_from', dateFrom);
      if (dateTo)       params.set('date_to', dateTo);

      const res = await api.get(`/collections/?${params.toString()}`);
      const rows = res.data.results || [];
      setCollections(rows);
      setTotalCount(res.data.count || 0);
      setCurrentPage(page);
      // total_amount from backend covers ALL matching records, not just this page
      setTotal(res.data.total_amount || 0);
    } catch (err) {
      console.error(err);
      setCollections([]);
      setTotal(0);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [pageSize, searchTerm, methodFilter, proofFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchCollections(1);

    api.get('/users/me/').then(res => {
      const u = res.data;
      if (u.is_superuser) setUserRole('admin');
      else if (u.is_manager) setUserRole('manager');
      else setUserRole(u.role || 'user');
    }).catch(() => {});
  }, [fetchCollections]);

  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    fetchCollections(page);
  };

  const clearFilters = () => {
    setMethodFilter('');
    setProofFilter('');
    setDateFrom('');
    setDateTo('');
    setSearchTerm('');
  };

  const hasActiveFilters = searchTerm || methodFilter || proofFilter || dateFrom || dateTo;

  const openEditModal = (entry) => {
    setEditingEntry(entry);
    setEditForm({
      amount_collected: entry.amount_collected,
      payment_method: entry.payment_method,
      transaction_id: entry.transaction_id || '',
      notes: entry.notes || '',
    });
  };

  const handleEditChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleEditSave = async () => {
    if (!editingEntry) return;
    setSaving(true);
    try {
      await api.patch(`/collections/${editingEntry.id}/`, {
        amount_collected: parseFloat(editForm.amount_collected),
        payment_method: editForm.payment_method,
        transaction_id: editForm.transaction_id || null,
        notes: editForm.notes || '',
      });
      setEditingEntry(null);
      fetchCollections(currentPage);
    } catch (err) {
      console.error('Failed to update entry:', err);
      alert(err.response?.data?.detail || 'Failed to update entry.');
    } finally {
      setSaving(false);
    }
  };

  const handleApproveProof = async (entryId) => {
    setApprovingId(entryId);
    try {
      await api.post(`/collections/${entryId}/approve-proof/`);
      setCollections(prev => prev.map(c =>
        c.id === entryId ? { ...c, payment_proof_status: 'verified' } : c
      ));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to approve proof.');
    } finally {
      setApprovingId(null);
    }
  };

  // Customer search for add modal
  const searchCustomers = async (query) => {
    setCustomerSearch(query);
    if (query.length < 2) {
      setCustomerResults([]);
      return;
    }
    setSearchingCustomers(true);
    try {
      const res = await api.get(`/officers/search-customers/?username=${encodeURIComponent(query)}`);
      setCustomerResults(res.data || []);
    } catch (err) {
      console.error('Customer search failed:', err);
      setCustomerResults([]);
    } finally {
      setSearchingCustomers(false);
    }
  };

  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.username);
    setCustomerResults([]);
  };

  const openAddModal = () => {
    setAddForm({ amount_collected: '', payment_method: 'cash', transaction_id: '', notes: '', collected_at: '' });
    setAddProofImage(null);
    setCustomerSearch('');
    setSelectedCustomer(null);
    setCustomerResults([]);
    setShowAddModal(true);
  };

  const handleAddChange = (field, value) => {
    setAddForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAddSave = async () => {
    if (!selectedCustomer) {
      alert('Please select a customer.');
      return;
    }
    if (!addForm.amount_collected || parseFloat(addForm.amount_collected) <= 0) {
      alert('Please enter a valid amount.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        customer: selectedCustomer.id,
        amount_collected: parseFloat(addForm.amount_collected),
        payment_method: addForm.payment_method,
        transaction_id: addForm.transaction_id || null,
        notes: addForm.notes || '',
      };
      if (addForm.collected_at && userRole === 'admin') {
        payload.collected_at = new Date(addForm.collected_at).toISOString();
      }
      if (addProofImage) {
        const formData = new FormData();
        Object.entries(payload).forEach(([k, v]) => {
          if (v !== null && v !== undefined) formData.append(k, v);
        });
        formData.append('proof_image', addProofImage);
        await api.post('/collections/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await api.post('/collections/', payload);
      }
      setShowAddModal(false);
      fetchCollections(1);
    } catch (err) {
      console.error('Failed to add entry:', err);
      alert(err.response?.data?.detail || JSON.stringify(err.response?.data) || 'Failed to add entry.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">All Collections</h1>
          <p className="text-gray-500 mt-1">Global transaction history and revenue auditing.</p>
        </div>
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
          {canEdit && (
            <button onClick={openAddModal} className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
              <Plus size={16} /> Add Entry
            </button>
          )}
          <div className="bg-white px-5 py-3 rounded-xl border border-gray-200 shadow-sm flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <span className="text-xs text-gray-500 uppercase font-bold block">Total Revenue</span>
              <span className="text-lg font-bold text-gray-900">PKR {total.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-[1.5fr_1fr_1fr_1fr_1fr_auto] gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by Customer or Officer..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            value={methodFilter}
            onChange={(e) => setMethodFilter(e.target.value)}
            className="px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-gray-700"
          >
            {PAYMENT_METHODS.map(method => (
              <option key={method.value} value={method.value}>{method.label}</option>
            ))}
          </select>
          <select
            value={proofFilter}
            onChange={(e) => setProofFilter(e.target.value)}
            className="px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-gray-700"
          >
            {PROOF_STATUS_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-gray-700"
            aria-label="Date from"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-gray-700"
            aria-label="Date to"
          />
          <button
            type="button"
            onClick={clearFilters}
            disabled={!hasActiveFilters}
            className="flex items-center justify-center gap-2 px-5 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {hasActiveFilters ? <X size={18} /> : <Filter size={18} />}
            Clear
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Showing {collections.length.toLocaleString()} of {totalCount.toLocaleString()} collections for the selected filters.
        </p>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase font-semibold">
              <tr>
                <th className="p-4">Date & Time</th>
                <th className="p-4">Customer</th>
                <th className="p-4">Collected By</th>
                <th className="p-4">Method</th>
                <th className="p-4">TX ID</th>
                <th className="p-4">Proof</th>
                <th className="p-4 text-right">Amount</th>
                {canEdit && <th className="p-4 text-center">Action</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={canEdit ? 8 : 7} className="p-8 text-center">Loading transactions...</td></tr>
              ) : collections.length === 0 ? (
                <tr><td colSpan={canEdit ? 8 : 7} className="p-8 text-center text-gray-500">No transactions found.</td></tr>
              ) : (
                collections.map(c => (
                  <tr key={c.id} className="hover:bg-blue-50/30 transition-colors">
                    <td className="p-4 text-sm text-gray-600 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Calendar size={14} className="text-gray-400"/>
                        {new Date(c.collected_at).toLocaleDateString()}
                        <span className="text-gray-400 text-xs">
                          {new Date(c.collected_at).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}
                        </span>
                      </div>
                    </td>
                    <td className="p-4 font-semibold text-gray-900">{c.customer_username}</td>
                    <td className="p-4 text-sm text-gray-600 flex items-center gap-2">
                      <UserCheck size={14} className="text-gray-400" />
                      {c.officer_name || 'Admin'}
                    </td>
                    <td className="p-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-bold capitalize ${
                        c.payment_method === 'cash' ? 'bg-green-100 text-green-700' :
                        c.payment_method === 'jazzcash' ? 'bg-red-100 text-red-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {c.payment_method.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="p-4 text-sm font-mono text-gray-500">{c.transaction_id || '-'}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-1.5">
                        <ProofBadge status={c.payment_proof_status} />
                        {c.proof_image && (
                          <button
                            onClick={() => setProofImageUrl(c.proof_image)}
                            className="p-0.5 text-gray-400 hover:text-blue-600 transition-colors"
                            title="View screenshot"
                          >
                            <Image size={13} />
                          </button>
                        )}
                        {canEdit && c.payment_proof_status === 'flagged' && (
                          <button
                            onClick={() => handleApproveProof(c.id)}
                            disabled={approvingId === c.id}
                            className="flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold text-green-700 bg-green-50 border border-green-200 rounded hover:bg-green-100 transition-colors disabled:opacity-50"
                            title="Approve this flagged proof"
                          >
                            <CheckCircle size={10} />
                            {approvingId === c.id ? '...' : 'Approve'}
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-right font-bold text-gray-900">PKR {parseFloat(c.amount_collected).toLocaleString()}</td>
                    {canEdit && (
                      <td className="p-4 text-center">
                        <button onClick={() => openEditModal(c)} className="p-1.5 rounded-lg hover:bg-blue-100 text-blue-600 transition-colors" title="Edit entry">
                          <Pencil size={14} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      {totalCount > 0 && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mt-4">
          <div className="text-sm text-gray-600">
            Showing <span className="font-semibold">{startItem}</span>-<span className="font-semibold">{endItem}</span> of <span className="font-semibold">{totalCount.toLocaleString()}</span> collections
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-500">Per page:</span>
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                className="px-2 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
              >
                {PAGE_SIZE_OPTIONS.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button onClick={() => goToPage(1)} disabled={currentPage === 1} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed text-gray-600">
                  <ChevronsLeft size={16} />
                </button>
                <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed text-gray-600">
                  <ChevronLeft size={16} />
                </button>
                <span className="px-3 py-1 text-sm text-gray-700 font-medium">
                  {currentPage} / {totalPages}
                </span>
                <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed text-gray-600">
                  <ChevronRight size={16} />
                </button>
                <button onClick={() => goToPage(totalPages)} disabled={currentPage === totalPages} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed text-gray-600">
                  <ChevronsRight size={16} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingEntry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setEditingEntry(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-100">
              <h3 className="font-bold text-gray-900">Edit Collection Entry</h3>
              <p className="text-xs text-gray-500 mt-1">{editingEntry.customer_username} — {new Date(editingEntry.collected_at).toLocaleDateString()}</p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Amount Collected (PKR)</label>
                <input
                  type="number"
                  value={editForm.amount_collected}
                  onChange={(e) => handleEditChange('amount_collected', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Payment Method</label>
                <select
                  value={editForm.payment_method}
                  onChange={(e) => handleEditChange('payment_method', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="cash">Cash</option>
                  <option value="jazzcash">JazzCash</option>
                  <option value="easypaisa">EasyPaisa</option>
                  <option value="bank">Bank Transfer</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Transaction ID</label>
                <input
                  type="text"
                  value={editForm.transaction_id}
                  onChange={(e) => handleEditChange('transaction_id', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Notes</label>
                <textarea
                  value={editForm.notes}
                  onChange={(e) => handleEditChange('notes', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  placeholder="Optional"
                />
              </div>
            </div>
            <div className="p-5 border-t border-gray-100 flex gap-3 justify-end">
              <button onClick={() => setEditingEntry(null)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                Cancel
              </button>
              <button
                onClick={handleEditSave}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                <Save size={14} />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Proof Image Lightbox */}
      {proofImageUrl && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setProofImageUrl(null)}>
          <div className="relative max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setProofImageUrl(null)}
              className="absolute -top-3 -right-3 bg-white rounded-full p-1 shadow-lg text-gray-600 hover:text-gray-900 z-10"
            >
              <X size={16} />
            </button>
            <img
              src={proofImageUrl}
              alt="Payment proof"
              className="w-full rounded-xl shadow-2xl"
            />
          </div>
        </div>
      )}

      {/* Add Entry Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-100">
              <h3 className="font-bold text-gray-900">Add Collection Entry</h3>
              <p className="text-xs text-gray-500 mt-1">Record a new collection for a customer</p>
            </div>
            <div className="p-5 space-y-4">
              {/* Customer Search */}
              <div className="relative">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Customer</label>
                <input
                  type="text"
                  value={customerSearch}
                  onChange={(e) => searchCustomers(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Type username to search..."
                />
                {customerResults.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {customerResults.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => selectCustomer(c)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors flex items-center justify-between"
                      >
                        <span className="font-semibold text-gray-900">{c.username}</span>
                        <span className="text-xs text-gray-500">{c.area_name || 'N/A'} · {c.package_name || 'N/A'}</span>
                      </button>
                    ))}
                  </div>
                )}
                {selectedCustomer && (
                  <p className="text-xs text-green-600 mt-1">Selected: {selectedCustomer.username} ({selectedCustomer.area_name || 'N/A'})</p>
                )}
              </div>

              {/* Amount */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Amount Collected (PKR)</label>
                <input
                  type="number"
                  value={addForm.amount_collected}
                  onChange={(e) => handleAddChange('amount_collected', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                />
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Payment Method</label>
                <select
                  value={addForm.payment_method}
                  onChange={(e) => handleAddChange('payment_method', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="cash">Cash</option>
                  <option value="jazzcash">JazzCash</option>
                  <option value="easypaisa">EasyPaisa</option>
                  <option value="bank">Bank Transfer</option>
                </select>
              </div>

              {/* Transaction ID */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Transaction ID</label>
                <input
                  type="text"
                  value={addForm.transaction_id}
                  onChange={(e) => handleAddChange('transaction_id', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Optional"
                />
              </div>

              {/* Proof Image — for non-cash payments */}
              {['jazzcash', 'easypaisa', 'bank'].includes(addForm.payment_method) && (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Payment Proof</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setAddProofImage(e.target.files[0] || null)}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  {addProofImage && <p className="text-xs text-green-600 mt-1">{addProofImage.name}</p>}
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Notes</label>
                <textarea
                  value={addForm.notes}
                  onChange={(e) => handleAddChange('notes', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  placeholder="Optional"
                />
              </div>

              {/* Collected At — admin only */}
              {userRole === 'admin' && (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Collected At</label>
                  <input
                    type="datetime-local"
                    value={addForm.collected_at}
                    onChange={(e) => handleAddChange('collected_at', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">Leave empty to use current date & time</p>
                </div>
              )}
            </div>
            <div className="p-5 border-t border-gray-100 flex gap-3 justify-end">
              <button onClick={() => setShowAddModal(false)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                Cancel
              </button>
              <button
                onClick={handleAddSave}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                <Plus size={14} />
                {saving ? 'Adding...' : 'Add Entry'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllCollections;

